import { css } from '@emotion/react';
import { faKey } from '@fortawesome/pro-solid-svg-icons';
import {
  Accordion, useSections,
} from '~/_shared/baseComponents/accordion';
import { FontAwesomeIcon } from '~/_shared/baseComponents/icon/fontAwesomeIcon.component';
import { EmptyStateComponent } from '~/_shared/components/emptyState/emptyState.component';
import {
  LOCATION_DESCRIPTION_HEADER_HEIGHT, LocationListingHeaderComponent,
} from '~/_shared/components/locationListingPanel/locationListingHeader.component';
import {
  ScrollBarComponent, ScrollbarType,
} from '~/_shared/components/scrollbar/scrollbar.component';
import type { Theme } from '~/_shared/themes/theme.model';
import { MapTool } from '~/_shared/types/toolsAndFeatures/mapTools.types';
import { noop } from '~/_shared/utils/function.helpers';
import { useTranslation } from '~/_shared/utils/hooks';
import { useIsMobileScreenSelector } from '~/store/frontendState/deviceInfo/deviceInfo.selector';
import {
  mapToolIconMap, mapToolTitleMap,
} from '../../mapTools/mapTools.helpers';
import { BoundaryKey } from './boundaryKey/boundaryKey';
import { GroupingKey } from './groupingKey';

const absoluteListingWrapper = (props: { showScrollbar: boolean }) => css({
  position: props.showScrollbar ? 'absolute' : 'unset',
  height: `calc(100% - ${LOCATION_DESCRIPTION_HEADER_HEIGHT}px)`,
  left: 0,
  top: LOCATION_DESCRIPTION_HEADER_HEIGHT,
  width: '100%',
});

const relativeListingWrapper = css({
  position: 'relative',
  height: '100%',
  width: '100%',
});

const headingStyle = css({
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  textTransform: 'uppercase',
});

const sectionContentStyle = (theme: Theme) => css({
  border: `1px solid ${theme.borderColors.primary}`,
  borderRadius: 4,
  overflow: 'hidden',
});

const emptyStateComponentStyle = css({
  padding: '50px 30px 16px',
});

export const MasterKeyComponent = ({
  allowScrollbar = true,
  allowShowTabs,
  isGroupingActive,
  hasBoundaryGroups,
  onClose,
}: {
  allowScrollbar?: boolean;
  allowShowTabs: boolean;
  isGroupingActive: boolean;
  hasBoundaryGroups: boolean;
  onClose?: () => void;
}) => {
  const [t] = useTranslation();
  const isMobileScreen = useIsMobileScreenSelector();

  const showScrollbar = allowScrollbar && !isMobileScreen;
  const isNoToolActive = !isGroupingActive && !hasBoundaryGroups;

  const { toggleSection, isExpanded } = useSections<MapTool>([MapTool.Grouping, MapTool.Boundary]);

  const renderAccordion = () => (
    <Accordion>
      {isGroupingActive && (
        <Accordion.Item
          key={MapTool.Grouping}
          onToggle={() => toggleSection(MapTool.Grouping)}
          open={isExpanded(MapTool.Grouping)}
        >
          <Accordion.ItemHeading>
            <div css={headingStyle}>
              <FontAwesomeIcon icon={mapToolIconMap[MapTool.Grouping]} />
              {t(mapToolTitleMap.Grouping)}
            </div>
          </Accordion.ItemHeading>
          <Accordion.Panel>
            <div css={sectionContentStyle}>
              <GroupingKey />
            </div>
          </Accordion.Panel>
        </Accordion.Item>
      )}
      {hasBoundaryGroups && (
        <Accordion.Item
          key={MapTool.Boundary}
          onToggle={() => toggleSection(MapTool.Boundary)}
          open={isExpanded(MapTool.Boundary)}
        >
          <Accordion.ItemHeading>
            <div css={headingStyle}>
              <FontAwesomeIcon icon={mapToolIconMap[MapTool.Boundary]} />
              {t(mapToolTitleMap.Boundary)}
            </div>
          </Accordion.ItemHeading>
          <Accordion.Panel>
            <div css={sectionContentStyle}>
              <BoundaryKey />
            </div>
          </Accordion.Panel>
        </Accordion.Item>
      )}
    </Accordion>
  );

  return (
    <>
      {(allowShowTabs || onClose) && (
        <LocationListingHeaderComponent
          showTabs={allowShowTabs}
          onCloseClick={onClose || noop}
        />
      )}
      <div css={absoluteListingWrapper({ showScrollbar })} >
        <div
          css={relativeListingWrapper}
        >
          {!isNoToolActive && showScrollbar && (
            <ScrollBarComponent type={ScrollbarType.Vertical}>
              {renderAccordion()}
            </ScrollBarComponent>
          )}
          {!isNoToolActive && !showScrollbar && renderAccordion()}
          {isNoToolActive && (
            <EmptyStateComponent
              css={emptyStateComponentStyle}
              icon={faKey}
              title={t('masterKey.noToolsAvailable.title')}
              description={t('masterKey.noToolsAvailable.desc')}
            />
          )}
        </div>
      </div>
    </>
  );
};
